import React, { useEffect, useState, useCallback, useRef } from "react";
import API from "../../../services/api";

const statusColors = {
  New: "bg-primary-subtle text-primary",
  InProgress: "bg-warning-subtle text-warning",
  OnHold: "bg-secondary-subtle text-secondary",
  Resolved: "bg-success-subtle text-success",
  Closed: "bg-dark-subtle text-dark",
  Escalated: "bg-danger-subtle text-danger",
};

const priorityColors = {
  LOW: "bg-success-subtle text-success",
  MEDIUM: "bg-warning-subtle text-warning",
  HIGH: "bg-warning-subtle text-warning",
  CRITICAL: "bg-danger-subtle text-danger",
};

const levelLabel = (level) => {
  if (level === 1) return { label: "Level 1 - Staff", color: "text-info" };
  if (level === 2) return { label: "Level 2 - Manager", color: "text-warning" };
  return { label: "Level 3 - Admin", color: "text-danger" };
};

const Grievances = () => {
  const [grievances, setGrievances] = useState([]);
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [villList, setVillList] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [showForward, setShowForward] = useState(false);
  const [showNote, setShowNote] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [detailItem, setDetailItem] = useState(null);
  const [history, setHistory] = useState([]);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  // Village autocomplete
  const [villSearch, setVillSearch] = useState("");
  const [villDropdown, setVillDropdown] = useState([]);
  const [showVillDrop, setShowVillDrop] = useState(false);
  const [selectedVill, setSelectedVill] = useState(null);
  const villRef = useRef(null);

  // Voter search
  const [voterSearch, setVoterSearch] = useState("");
  const [voterList, setVoterList] = useState([]);
  const [voterLoading, setVoterLoading] = useState(false);
  const [showVoterDrop, setShowVoterDrop] = useState(false);
  const voterRef = useRef(null);
  const searchTimer = useRef(null);

  // Forward
  const [forwardUserId, setForwardUserId] = useState("");
  const [forwardNote, setForwardNote] = useState("");
  const [forwardAction, setForwardAction] = useState("forward");
  const [forwarding, setForwarding] = useState(false);

  // Note
  const [noteText, setNoteText] = useState("");
  const [addingNote, setAddingNote] = useState(false);

  const emptyForm = {
    title: "",
    description: "",
    reporter_name: "",
    reporter_mobile: "",
    reporter_address: "",
    grvcatid: "",
    deptid: "",
    villageid: "",
    priority: "LOW",
    epic_no: "",
    current_status: "New",
    assignee_user_id: "",
    age: "",
    gender: "",
  };
  const [form, setForm] = useState(emptyForm);
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);

  // ── Fetch Masters ─────────────────────────────────────────
  const fetchMasters = useCallback(async () => {
    try {
      const [cats, depts, vill, users] = await Promise.all([
        API.get("/masters/grievance-categories"),
        API.get("/masters/departments"),
        API.get("/masters/villages"),
        API.get("/grievances/assignable-users"),
      ]);
      setCategories(cats.data.data);
      setDepartments(depts.data.data);
      setVillList(vill.data.data);
      setAssignableUsers(users.data.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  // ── Fetch Grievances ──────────────────────────────────────
  const fetchGrievances = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const res = await API.get("/grievances/", {
          params: {
            page,
            per_page: 10,
            search: search || undefined,
            status: filterStatus || undefined,
            priority: filterPriority || undefined,
          },
        });
        setGrievances(res.data.data.grievances);
        setTotalPages(res.data.data.pagination.total_pages);
        setTotalRecords(res.data.data.pagination.total);
        setCurrentPage(page);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    [search, filterStatus, filterPriority],
  );

  useEffect(() => {
    fetchMasters();
    fetchGrievances();
  }, []);

  // ── Close dropdowns on outside click ─────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (villRef.current && !villRef.current.contains(e.target))
        setShowVillDrop(false);
      if (voterRef.current && !voterRef.current.contains(e.target))
        setShowVoterDrop(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Village Autocomplete ──────────────────────────────────
  const handleVillSearch = (val) => {
    setVillSearch(val);
    setSelectedVill(null);
    setForm((prev) => ({ ...prev, villageid: "" }));
    setVoterSearch("");
    setVoterList([]);
    setShowVoterDrop(false);
    if (val.length >= 1) {
      const filtered = villList.filter((v) =>
        v.name.toLowerCase().includes(val.toLowerCase()),
      );
      setVillDropdown(filtered.slice(0, 8));
      setShowVillDrop(true);
    } else {
      setVillDropdown([]);
      setShowVillDrop(false);
    }
  };

  const selectVillage = (village) => {
    setVillSearch(village.name);
    setSelectedVill(village);
    setForm((prev) => ({ ...prev, villageid: village.villageid }));
    setShowVillDrop(false);
    setVillDropdown([]);
    setVoterSearch("");
    setVoterList([]);
    setShowVoterDrop(false);
  };

  // ── Voter Search ──────────────────────────────────────────
  const handleVoterSearch = (val) => {
    setVoterSearch(val);
    setShowVoterDrop(true);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (val.length >= 2) {
      setVoterLoading(true);
      searchTimer.current = setTimeout(async () => {
        try {
          const res = await API.get("/voters/", {
            params: { page: 1, per_page: 15, search: val },
          });
          setVoterList(res.data.data.voters || []);
        } catch (err) {
          setVoterList([]);
        } finally {
          setVoterLoading(false);
        }
      }, 400);
    } else {
      setVoterList([]);
      setVoterLoading(false);
    }
  };

  const selectVoter = (voter) => {
    setForm((prev) => ({
      ...prev,
      reporter_name:
        `${voter.firstname_en || ""} ${voter.lastname_en || ""}`.trim(),
      reporter_mobile: voter.mobile || "",
      epic_no: voter.epic_no || "",
      age: voter.age || "",
      gender: voter.gender || "",
    }));
    setVoterSearch(
      `${voter.firstname_en || ""} ${voter.lastname_en || ""}`.trim(),
    );
    setShowVoterDrop(false);
    setVoterList([]);
  };

  // ── File Attachments ──────────────────────────────────────
  const MAX_FILE_MB = 2;
  const ALLOWED_TYPES = [
    "image/jpeg",
    "image/png",
    "image/jpg",
    "application/pdf",
  ];

  const handleFileAdd = (e) => {
    const files = Array.from(e.target.files);
    const newAttachments = [];
    files.forEach((file) => {
      if (!ALLOWED_TYPES.includes(file.type)) {
        alert(`${file.name}: Only JPG, PNG, PDF allowed`);
        return;
      }
      const sizeMB = file.size / (1024 * 1024);
      if (sizeMB > MAX_FILE_MB) {
        alert(`${file.name}: File exceeds ${MAX_FILE_MB}MB limit`);
        return;
      }
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (ev) => {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement("canvas");
            let w = img.width,
              h = img.height;
            const MAX = 1200;
            if (w > MAX || h > MAX) {
              if (w > h) {
                h = Math.round((h * MAX) / w);
                w = MAX;
              } else {
                w = Math.round((w * MAX) / h);
                h = MAX;
              }
            }
            canvas.width = w;
            canvas.height = h;
            canvas.getContext("2d").drawImage(img, 0, 0, w, h);
            canvas.toBlob(
              (blob) => {
                const compressed = new File([blob], file.name, {
                  type: "image/jpeg",
                });
                setAttachments((prev) => [
                  ...prev,
                  {
                    file: compressed,
                    preview: canvas.toDataURL("image/jpeg", 0.8),
                    name: file.name,
                    size: compressed.size,
                    type: "image",
                  },
                ]);
              },
              "image/jpeg",
              0.8,
            );
          };
          img.src = ev.target.result;
        };
        reader.readAsDataURL(file);
      } else {
        setAttachments((prev) => [
          ...prev,
          {
            file,
            preview: null,
            name: file.name,
            size: file.size,
            type: "pdf",
          },
        ]);
      }
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeAttachment = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  // ── Open Add ──────────────────────────────────────────────
  const openAdd = () => {
    setEditItem(null);
    setForm(emptyForm);
    setVillSearch("");
    setSelectedVill(null);
    setVoterSearch("");
    setVoterList([]);
    setAttachments([]);
    setShowVillDrop(false);
    setShowVoterDrop(false);
    setShowModal(true);
  };

  // ── Open Edit ─────────────────────────────────────────────
  const openEdit = (g) => {
    setEditItem(g);
    const vill = villList.find((v) => v.villageid === g.villageid);
    setVillSearch(vill ? vill.name : "");
    setSelectedVill(vill || null);
    setVoterSearch(g.reporter_name || "");
    setVoterList([]);
    setShowVillDrop(false);
    setShowVoterDrop(false);
    setForm({
      title: g.title || "",
      description: g.description || "",
      reporter_name: g.reporter_name || "",
      reporter_mobile: g.reporter_mobile || "",
      reporter_address: g.reporter_address || "",
      grvcatid: g.grvcatid || "",
      deptid: g.deptid || "",
      villageid: g.villageid || "",
      priority: g.priority || "LOW",
      epic_no: g.epic_no || "",
      current_status: g.current_status || "New",
      assignee_user_id: g.assignee_id || "",
    });
    setShowModal(true);
  };

  // ── Save ──────────────────────────────────────────────────
  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Upload attachments first
      const docIds = [];
      for (const att of attachments) {
        try {
          const fd = new FormData();
          fd.append("file", att.file);
          fd.append("reference", form.title || "GRIEVANCE");
          const res = await API.post("/grievances/upload-doc", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          if (res.data.data?.id) docIds.push(res.data.data.id);
        } catch (err) {
          console.warn("File upload failed:", att.name);
        }
      }

      const payload = {
        ...form,
        grvcatid: form.grvcatid ? parseInt(form.grvcatid) : null,
        deptid: form.deptid ? parseInt(form.deptid) : null,
        villageid: form.villageid ? parseInt(form.villageid) : null,
        assignee_user_id: form.assignee_user_id
          ? parseInt(form.assignee_user_id)
          : 1,
        age: undefined,
        gender: undefined,
        document_ids: docIds.length > 0 ? docIds : undefined,
      };
      if (editItem) {
        await API.put(`/grievances/${editItem.grievance_id}`, payload);
      } else {
        await API.post("/grievances/", payload);
      }
      setShowModal(false);
      setAttachments([]);
      fetchGrievances(currentPage);
    } catch (err) {
      alert(err.response?.data?.message || "Error saving grievance");
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this grievance?")) return;
    setDeleting(id);
    try {
      await API.delete(`/grievances/${id}`);
      fetchGrievances(currentPage);
    } catch (err) {
      alert(err.response?.data?.message || "Error");
    } finally {
      setDeleting(null);
    }
  };

  // ── Open Detail ───────────────────────────────────────────
  const openDetail = async (g) => {
    setDetailItem(g);
    setHistory([]);
    setShowDetail(true);
    setShowForward(false);
    setShowNote(false);
    try {
      const res = await API.get(`/grievances/${g.grievance_id}/history`);
      setHistory(res.data.data);
    } catch (err) {
      console.error(err);
    }
  };

  // ── Update Status ─────────────────────────────────────────
  const updateStatus = async (grievance_id, status) => {
    try {
      await API.put(`/grievances/${grievance_id}`, { current_status: status });
      const res = await API.get(`/grievances/${grievance_id}`);
      setDetailItem(res.data.data);
      fetchGrievances(currentPage);
    } catch (err) {
      alert(err.response?.data?.message || "Error");
    }
  };

  // ── Add Note ──────────────────────────────────────────────
  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    setAddingNote(true);
    try {
      await API.post(`/grievances/${detailItem.grievance_id}/note`, {
        note: noteText,
      });
      setNoteText("");
      setShowNote(false);
      const res = await API.get(`/grievances/${detailItem.grievance_id}`);
      setDetailItem(res.data.data);
      fetchGrievances(currentPage);
    } catch (err) {
      alert(err.response?.data?.message || "Error");
    } finally {
      setAddingNote(false);
    }
  };

  // ── Forward ───────────────────────────────────────────────
  const handleForward = async (e) => {
    e.preventDefault();
    if (!forwardUserId) return alert("Please select a user");
    setForwarding(true);
    try {
      await API.post(`/grievances/${detailItem.grievance_id}/assign`, {
        to_user_id: parseInt(forwardUserId),
        note: forwardNote,
        action: forwardAction,
      });
      setShowForward(false);
      setForwardUserId("");
      setForwardNote("");
      const res = await API.get(`/grievances/${detailItem.grievance_id}`);
      setDetailItem(res.data.data);
      const hist = await API.get(
        `/grievances/${detailItem.grievance_id}/history`,
      );
      setHistory(hist.data.data);
      fetchGrievances(currentPage);
    } catch (err) {
      alert(err.response?.data?.message || "Error forwarding");
    } finally {
      setForwarding(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h4 className="fw-bold mb-1">Grievances</h4>
          <p className="text-muted small mb-0">
            Total <strong>{totalRecords}</strong> grievances
          </p>
        </div>
        <button
          className="btn text-white fw-semibold px-4"
          style={{ background: "linear-gradient(135deg, #1a3c5e, #2d6a4f)" }}
          onClick={openAdd}
        >
          <i className="bi bi-plus-lg me-2"></i>Add Grievance
        </button>
      </div>

      {/* Filters */}
      <div className="card border-0 shadow-sm rounded-4 mb-3">
        <div className="card-body p-3">
          <div className="row g-2 align-items-end">
            <div className="col-12 col-md-4">
              <input
                type="text"
                className="form-control"
                placeholder="Search by title, ticket no, name, mobile..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="col-6 col-md-2">
              <select
                className="form-select"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="">All Status</option>
                {[
                  "New",
                  "InProgress",
                  "OnHold",
                  "Resolved",
                  "Closed",
                  "Escalated",
                ].map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6 col-md-2">
              <select
                className="form-select"
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
              >
                <option value="">All Priority</option>
                {["LOW", "MEDIUM", "HIGH", "CRITICAL"].map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6 col-md-2">
              <button
                className="btn btn-primary w-100"
                onClick={() => fetchGrievances(1)}
              >
                <i className="bi bi-search me-1"></i>Search
              </button>
            </div>
            <div className="col-6 col-md-2">
              <button
                className="btn btn-outline-secondary w-100"
                onClick={() => {
                  setSearch("");
                  setFilterStatus("");
                  setFilterPriority("");
                  fetchGrievances(1);
                }}
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary"></div>
            </div>
          ) : grievances.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-chat-left-text fs-1 d-block mb-2"></i>
              No grievances found.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="bg-light">
                  <tr>
                    <th className="px-4 py-3 small text-muted fw-semibold">
                      #
                    </th>
                    <th className="py-3 small text-muted fw-semibold">
                      Ticket
                    </th>
                    <th className="py-3 small text-muted fw-semibold">Title</th>
                    <th className="py-3 small text-muted fw-semibold">
                      Reporter
                    </th>
                    <th className="py-3 small text-muted fw-semibold">
                      Assigned To
                    </th>
                    <th className="py-3 small text-muted fw-semibold">
                      Priority
                    </th>
                    <th className="py-3 small text-muted fw-semibold">
                      Status
                    </th>
                    <th className="py-3 small text-muted fw-semibold">Level</th>
                    <th className="py-3 small text-muted fw-semibold text-end pe-4">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {grievances.map((g, idx) => (
                    <tr key={g.grievance_id}>
                      <td className="px-4 text-muted small">
                        {(currentPage - 1) * 10 + idx + 1}
                      </td>
                      <td>
                        <span className="badge bg-light text-dark border small fw-normal">
                          {g.ticket_no}
                        </span>
                      </td>
                      <td style={{ maxWidth: "180px" }}>
                        <div className="fw-semibold small text-truncate">
                          {g.title}
                        </div>
                        {g.category && (
                          <div
                            className="text-muted"
                            style={{ fontSize: "11px" }}
                          >
                            {g.category}
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="small fw-medium">
                          {g.reporter_name || "—"}
                        </div>
                        <div
                          className="text-muted"
                          style={{ fontSize: "11px" }}
                        >
                          {g.reporter_mobile || ""}
                        </div>
                      </td>
                      <td className="small">{g.assignee_name || "—"}</td>
                      <td>
                        <span
                          className={`badge small ${priorityColors[g.priority] || ""}`}
                        >
                          {g.priority}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`badge small ${statusColors[g.current_status] || ""}`}
                        >
                          {g.current_status}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`small fw-medium ${levelLabel(g.current_level).color}`}
                        >
                          L{g.current_level}
                        </span>
                      </td>
                      <td className="text-end pe-4">
                        <button
                          className="btn btn-sm btn-outline-info me-1"
                          onClick={() => openDetail(g)}
                          title="View"
                        >
                          <i className="bi bi-eye"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-primary me-1"
                          onClick={() => openEdit(g)}
                          title="Edit"
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(g.grievance_id)}
                          disabled={deleting === g.grievance_id}
                        >
                          {deleting === g.grievance_id ? (
                            <span className="spinner-border spinner-border-sm"></span>
                          ) : (
                            <i className="bi bi-trash"></i>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="card-footer bg-white border-0 d-flex justify-content-between align-items-center py-3 px-4">
            <span className="text-muted small">
              Page {currentPage} of {totalPages}
            </span>
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => fetchGrievances(currentPage - 1)}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>
                {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                  const page = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                  if (page > totalPages) return null;
                  return (
                    <li
                      key={page}
                      className={`page-item ${currentPage === page ? "active" : ""}`}
                    >
                      <button
                        className="page-link"
                        onClick={() => fetchGrievances(page)}
                      >
                        {page}
                      </button>
                    </li>
                  );
                })}
                <li
                  className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => fetchGrievances(currentPage + 1)}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>

      {/* ── Detail Modal ── */}
      {showDetail && detailItem && (
        <div
          className="modal show d-block"
          style={{ background: "rgba(0,0,0,0.6)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-xl">
            <div className="modal-content border-0 rounded-4 shadow-lg">
              <div
                className="modal-header border-0 px-4 pt-4 pb-2"
                style={{
                  background: "linear-gradient(135deg, #1a3c5e, #2d6a4f)",
                  borderRadius: "1rem 1rem 0 0",
                }}
              >
                <div>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <span className="badge bg-white text-dark small">
                      {detailItem.ticket_no}
                    </span>
                    <span
                      className={`badge small ${statusColors[detailItem.current_status]}`}
                    >
                      {detailItem.current_status}
                    </span>
                    <span
                      className={`badge small ${priorityColors[detailItem.priority]}`}
                    >
                      {detailItem.priority}
                    </span>
                    <span className="badge bg-white bg-opacity-25 text-white small">
                      {levelLabel(detailItem.current_level).label}
                    </span>
                  </div>
                  <h5 className="modal-title fw-bold text-white mb-0">
                    {detailItem.title}
                  </h5>
                </div>
                <button
                  className="btn-close btn-close-white"
                  onClick={() => setShowDetail(false)}
                ></button>
              </div>

              <div
                className="modal-body px-4 py-3"
                style={{ maxHeight: "70vh", overflowY: "auto" }}
              >
                <div className="row g-3">
                  {/* Left */}
                  <div className="col-12 col-lg-7">
                    <div className="card border-0 bg-light rounded-3 mb-3">
                      <div className="card-body p-3">
                        <h6 className="fw-semibold small mb-2">
                          <i className="bi bi-person me-1"></i>Reporter Details
                        </h6>
                        <div className="row g-2">
                          <div className="col-6">
                            <div
                              className="text-muted"
                              style={{ fontSize: "11px" }}
                            >
                              Name
                            </div>
                            <div className="small fw-medium">
                              {detailItem.reporter_name || "—"}
                            </div>
                          </div>
                          <div className="col-6">
                            <div
                              className="text-muted"
                              style={{ fontSize: "11px" }}
                            >
                              Mobile
                            </div>
                            <div className="small fw-medium">
                              {detailItem.reporter_mobile || "—"}
                            </div>
                          </div>
                          <div className="col-6">
                            <div
                              className="text-muted"
                              style={{ fontSize: "11px" }}
                            >
                              EPIC No
                            </div>
                            <div className="small fw-medium">
                              {detailItem.epic_no || "—"}
                            </div>
                          </div>
                          <div className="col-6">
                            <div
                              className="text-muted"
                              style={{ fontSize: "11px" }}
                            >
                              Category
                            </div>
                            <div className="small fw-medium">
                              {detailItem.category || "—"}
                            </div>
                          </div>
                          {detailItem.reporter_address && (
                            <div className="col-12">
                              <div
                                className="text-muted"
                                style={{ fontSize: "11px" }}
                              >
                                Address
                              </div>
                              <div className="small">
                                {detailItem.reporter_address}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="card border-0 bg-light rounded-3 mb-3">
                      <div className="card-body p-3">
                        <h6 className="fw-semibold small mb-2">
                          <i className="bi bi-text-left me-1"></i>Description &
                          Notes
                        </h6>
                        <div
                          className="small text-muted"
                          style={{
                            whiteSpace: "pre-line",
                            maxHeight: "150px",
                            overflowY: "auto",
                          }}
                        >
                          {detailItem.description || "No description."}
                        </div>
                      </div>
                    </div>

                    {/* Documents */}
                    {detailItem.documents &&
                      detailItem.documents.length > 0 && (
                        <div className="card border-0 bg-light rounded-3 mb-3">
                          <div className="card-body p-3">
                            <h6 className="fw-semibold small mb-2">
                              <i className="bi bi-paperclip me-1"></i>
                              Attachments ({detailItem.documents.length})
                            </h6>
                            <div className="d-flex flex-wrap gap-2">
                              {detailItem.documents.map((doc) => (
                                <a
                                  key={doc.id}
                                  href={`http://localhost:5000/api/grievances/download/${doc.id}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                                >
                                  {doc.doctype === "PDF" ? (
                                    <i className="bi bi-file-earmark-pdf text-danger"></i>
                                  ) : (
                                    <i className="bi bi-image text-success"></i>
                                  )}
                                  <span
                                    className="text-truncate small"
                                    style={{ maxWidth: "120px" }}
                                  >
                                    {doc.docname}
                                  </span>
                                  <i className="bi bi-download ms-1"></i>
                                </a>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                    <div className="card border-0 bg-light rounded-3">
                      <div className="card-body p-3">
                        <h6 className="fw-semibold small mb-2">
                          <i className="bi bi-clock-history me-1"></i>Assignment
                          History
                        </h6>
                        {history.length === 0 ? (
                          <div className="text-muted small">
                            No history yet.
                          </div>
                        ) : (
                          history.map((h, idx) => (
                            <div
                              key={h.id}
                              className="d-flex gap-2 mb-2 pb-2 border-bottom"
                            >
                              <div
                                className="rounded-circle bg-primary d-flex align-items-center justify-content-center text-white flex-shrink-0"
                                style={{
                                  width: 26,
                                  height: 26,
                                  fontSize: "11px",
                                }}
                              >
                                {idx + 1}
                              </div>
                              <div>
                                <div className="small fw-medium">
                                  {h.from_user} →{" "}
                                  <span className="text-primary">
                                    {h.to_user}
                                  </span>
                                </div>
                                {h.reason && (
                                  <div
                                    className="text-muted"
                                    style={{ fontSize: "11px" }}
                                  >
                                    {h.reason}
                                  </div>
                                )}
                                <div
                                  className="text-muted"
                                  style={{ fontSize: "10px" }}
                                >
                                  {h.assigned_at}
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right — Actions */}
                  <div className="col-12 col-lg-5">
                    <div className="card border-0 bg-light rounded-3 mb-3">
                      <div className="card-body p-3">
                        <h6 className="fw-semibold small mb-2">
                          <i className="bi bi-arrow-repeat me-1"></i>Update
                          Status
                        </h6>
                        <div className="d-flex flex-wrap gap-2">
                          {[
                            "New",
                            "InProgress",
                            "OnHold",
                            "Resolved",
                            "Closed",
                          ].map((s) => (
                            <button
                              key={s}
                              className={`btn btn-sm ${
                                detailItem.current_status === s
                                  ? "text-white"
                                  : "btn-outline-secondary"
                              }`}
                              style={
                                detailItem.current_status === s
                                  ? {
                                      background:
                                        "linear-gradient(135deg, #1a3c5e, #2d6a4f)",
                                    }
                                  : {}
                              }
                              onClick={() =>
                                updateStatus(detailItem.grievance_id, s)
                              }
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                        <div className="mt-2 small text-muted">
                          Assigned to:{" "}
                          <strong>{detailItem.assignee_name || "—"}</strong>
                        </div>
                        {detailItem.resolved_at && (
                          <div className="small text-success mt-1">
                            <i className="bi bi-check-circle me-1"></i>
                            Resolved: {detailItem.resolved_at}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="card border-0 bg-light rounded-3 mb-3">
                      <div className="card-body p-3">
                        <h6 className="fw-semibold small mb-2">
                          <i className="bi bi-chat-square-text me-1"></i>Add
                          Note
                        </h6>
                        {showNote ? (
                          <form onSubmit={handleAddNote}>
                            <textarea
                              className="form-control form-control-sm mb-2"
                              rows={3}
                              placeholder="Type your note here..."
                              value={noteText}
                              onChange={(e) => setNoteText(e.target.value)}
                              required
                            />
                            <div className="d-flex gap-2">
                              <button
                                type="submit"
                                className="btn btn-sm text-white"
                                style={{
                                  background:
                                    "linear-gradient(135deg, #1a3c5e, #2d6a4f)",
                                }}
                                disabled={addingNote}
                              >
                                {addingNote ? (
                                  <span className="spinner-border spinner-border-sm"></span>
                                ) : (
                                  "Save Note"
                                )}
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-light"
                                onClick={() => {
                                  setShowNote(false);
                                  setNoteText("");
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          </form>
                        ) : (
                          <button
                            className="btn btn-sm btn-outline-primary w-100"
                            onClick={() => setShowNote(true)}
                          >
                            <i className="bi bi-plus me-1"></i>Add Note
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="card border-0 bg-light rounded-3">
                      <div className="card-body p-3">
                        <h6 className="fw-semibold small mb-2">
                          <i className="bi bi-arrow-right-circle me-1"></i>
                          Forward / Assign
                        </h6>
                        {showForward ? (
                          <form onSubmit={handleForward}>
                            <div className="mb-2">
                              <select
                                className="form-select form-select-sm"
                                value={forwardAction}
                                onChange={(e) =>
                                  setForwardAction(e.target.value)
                                }
                              >
                                <option value="forward">
                                  ⬆️ Forward (Escalate)
                                </option>
                                <option value="assign">
                                  👤 Assign to someone
                                </option>
                              </select>
                            </div>
                            <div className="mb-2">
                              <select
                                className="form-select form-select-sm"
                                value={forwardUserId}
                                onChange={(e) =>
                                  setForwardUserId(e.target.value)
                                }
                                required
                              >
                                <option value="">Select user...</option>
                                {assignableUsers.map((u) => (
                                  <option key={u.id} value={u.id}>
                                    {u.name} ({u.role})
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="mb-2">
                              <textarea
                                className="form-control form-control-sm"
                                rows={2}
                                placeholder="Reason / Note (optional)"
                                value={forwardNote}
                                onChange={(e) => setForwardNote(e.target.value)}
                              />
                            </div>
                            <div className="d-flex gap-2">
                              <button
                                type="submit"
                                className="btn btn-sm text-white"
                                style={{
                                  background:
                                    "linear-gradient(135deg, #1a3c5e, #2d6a4f)",
                                }}
                                disabled={forwarding}
                              >
                                {forwarding ? (
                                  <span className="spinner-border spinner-border-sm"></span>
                                ) : forwardAction === "forward" ? (
                                  "⬆️ Forward"
                                ) : (
                                  "👤 Assign"
                                )}
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-light"
                                onClick={() => {
                                  setShowForward(false);
                                  setForwardUserId("");
                                  setForwardNote("");
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          </form>
                        ) : (
                          <button
                            className="btn btn-sm btn-outline-warning w-100"
                            onClick={() => setShowForward(true)}
                          >
                            <i className="bi bi-arrow-right-circle me-1"></i>
                            Forward / Reassign
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer border-0 px-4 pb-3">
                <div className="text-muted small me-auto">
                  Created by <strong>{detailItem.created_by_name}</strong> on{" "}
                  {detailItem.created_at}
                </div>
                <button
                  className="btn btn-light px-4"
                  onClick={() => setShowDetail(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Add/Edit Modal ── */}
      {showModal && (
        <div
          className="modal show d-block"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 rounded-4 shadow-lg">
              <div className="modal-header border-0 px-4 pt-4 pb-0">
                <h5 className="modal-title fw-bold">
                  {editItem ? "Edit Grievance" : "Add Grievance"}
                </h5>
                <button
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <form onSubmit={handleSave}>
                <div
                  className="modal-body px-4 py-3"
                  style={{ maxHeight: "70vh", overflowY: "auto" }}
                >
                  {/* ── STEP 1: Village ── */}
                  <div
                    className="p-3 rounded-3 mb-3"
                    style={{ background: "#f0f4ff" }}
                  >
                    <p
                      className="small fw-semibold mb-2"
                      style={{ color: "#1a3c5e" }}
                    >
                      <i className="bi bi-geo-alt me-1"></i>Step 1 — Select
                      Village
                    </p>
                    <div className="position-relative" ref={villRef}>
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0">
                          <i className="bi bi-search text-muted"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control border-start-0"
                          placeholder="Type village name..."
                          value={villSearch}
                          onChange={(e) => handleVillSearch(e.target.value)}
                          autoComplete="off"
                        />
                        {selectedVill && (
                          <span className="input-group-text bg-white">
                            <i className="bi bi-check-circle-fill text-success"></i>
                          </span>
                        )}
                      </div>
                      {showVillDrop && villDropdown.length > 0 && (
                        <div
                          className="border rounded-3 shadow bg-white position-absolute w-100 mt-1"
                          style={{
                            zIndex: 9999,
                            maxHeight: "160px",
                            overflowY: "auto",
                          }}
                        >
                          {villDropdown.map((v) => (
                            <div
                              key={v.villageid}
                              className="px-3 py-2 border-bottom d-flex align-items-center gap-2"
                              style={{ cursor: "pointer" }}
                              onMouseDown={() => selectVillage(v)}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.background = "#f0f4ff")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.background = "white")
                              }
                            >
                              <i className="bi bi-geo-alt text-muted small"></i>
                              <span className="small fw-medium">{v.name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {villSearch &&
                        villDropdown.length === 0 &&
                        showVillDrop && (
                          <div
                            className="border rounded-3 bg-white position-absolute w-100 mt-1 px-3 py-2"
                            style={{ zIndex: 9999 }}
                          >
                            <span className="text-muted small">
                              No village found
                            </span>
                          </div>
                        )}
                    </div>
                    {selectedVill && (
                      <div className="mt-2 small text-success">
                        <i className="bi bi-check-circle me-1"></i>
                        Village: <strong>{selectedVill.name}</strong>
                      </div>
                    )}
                  </div>

                  {/* ── STEP 2: Voter Search ── */}
                  <div
                    className="p-3 rounded-3 mb-3"
                    style={{ background: selectedVill ? "#f0f4ff" : "#f8f9fa" }}
                  >
                    <p
                      className="small fw-semibold mb-2"
                      style={{ color: selectedVill ? "#1a3c5e" : "#aaa" }}
                    >
                      <i className="bi bi-person-search me-1"></i>
                      Step 2 — Search Reporter from{" "}
                      {selectedVill ? (
                        <strong>{selectedVill.name}</strong>
                      ) : (
                        "selected village"
                      )}
                    </p>
                    <div className="position-relative" ref={voterRef}>
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0">
                          <i className="bi bi-search text-muted"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control border-start-0"
                          placeholder={
                            selectedVill
                              ? `Search voter in ${selectedVill.name}...`
                              : "Select village first..."
                          }
                          value={voterSearch}
                          onChange={(e) => handleVoterSearch(e.target.value)}
                          disabled={!selectedVill}
                          autoComplete="off"
                        />
                        {voterLoading && (
                          <span className="input-group-text bg-white">
                            <span className="spinner-border spinner-border-sm text-primary"></span>
                          </span>
                        )}
                      </div>
                      <div className="form-text">
                        {selectedVill
                          ? "Type 2+ chars — name (English/Kannada) or EPIC no"
                          : "Select village above first"}
                      </div>
                      {showVoterDrop && voterList.length > 0 && (
                        <div
                          className="border rounded-3 shadow bg-white position-absolute w-100 mt-1"
                          style={{
                            zIndex: 9999,
                            maxHeight: "220px",
                            overflowY: "auto",
                          }}
                        >
                          {voterList.map((v) => (
                            <div
                              key={v.voter_id}
                              className="px-3 py-2 border-bottom d-flex justify-content-between align-items-center"
                              style={{ cursor: "pointer" }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.background = "#f0f4ff")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.background = "white")
                              }
                            >
                              <div>
                                <div className="fw-semibold small">
                                  {v.firstname_en} {v.lastname_en}
                                </div>
                                {(v.firstname_v1 || v.lastname_v1) && (
                                  <div
                                    className="text-muted"
                                    style={{ fontSize: "12px" }}
                                  >
                                    {v.firstname_v1} {v.lastname_v1}
                                  </div>
                                )}
                                <div
                                  className="d-flex flex-wrap gap-1 align-items-center mt-1"
                                  style={{ fontSize: "11px" }}
                                >
                                  <span className="badge bg-primary-subtle text-primary">
                                    {v.epic_no}
                                  </span>
                                  {v.age && (
                                    <span className="badge bg-secondary-subtle text-secondary">
                                      <i className="bi bi-person me-1"></i>
                                      {v.age} yrs
                                    </span>
                                  )}
                                  {v.gender && (
                                    <span className="badge bg-light text-dark border">
                                      {v.gender}
                                    </span>
                                  )}
                                  {v.mobile && (
                                    <span className="badge bg-success-subtle text-success">
                                      📱 {v.mobile}
                                    </span>
                                  )}
                                  <span className="badge bg-light text-dark border">
                                    Booth {v.booth_no}
                                  </span>
                                </div>
                              </div>
                              <button
                                type="button"
                                className="btn btn-sm ms-2 text-white flex-shrink-0"
                                style={{
                                  background:
                                    "linear-gradient(135deg, #1a3c5e, #2d6a4f)",
                                }}
                                onMouseDown={() => selectVoter(v)}
                              >
                                <i className="bi bi-plus-lg me-1"></i>Select
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      {showVoterDrop &&
                        voterSearch.length >= 2 &&
                        voterList.length === 0 &&
                        !voterLoading && (
                          <div
                            className="border rounded-3 bg-white position-absolute w-100 mt-1 px-3 py-2"
                            style={{ zIndex: 9999 }}
                          >
                            <span className="text-muted small">
                              No voters found
                            </span>
                          </div>
                        )}
                    </div>

                    {/* Auto-filled reporter info */}
                    {form.reporter_name && (
                      <div className="row g-2 mt-2">
                        <div className="col-12 col-md-4">
                          <label className="form-label small fw-medium mb-1">
                            Reporter Name
                          </label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={form.reporter_name}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                reporter_name: e.target.value,
                              })
                            }
                          />
                        </div>
                        <div className="col-6 col-md-4">
                          <label className="form-label small fw-medium mb-1">
                            Mobile
                          </label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            maxLength={10}
                            value={form.reporter_mobile}
                            onChange={(e) => {
                              const val = e.target.value.replace(/\D/g, "");
                              setForm({ ...form, reporter_mobile: val });
                            }}
                          />
                        </div>
                        <div className="col-6 col-md-4">
                          <label className="form-label small fw-medium mb-1">
                            EPIC No
                          </label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={form.epic_no}
                            onChange={(e) =>
                              setForm({ ...form, epic_no: e.target.value })
                            }
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ── STEP 3: Grievance Details ── */}
                  <div
                    className="p-3 rounded-3"
                    style={{ background: "#f8f9fa" }}
                  >
                    <p
                      className="small fw-semibold mb-3"
                      style={{ color: "#1a3c5e" }}
                    >
                      <i className="bi bi-chat-left-text me-1"></i>Step 3 —
                      Grievance Details
                    </p>
                    <div className="row g-3">
                      <div className="col-12">
                        <label className="form-label small fw-medium">
                          Title <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter grievance title"
                          value={form.title}
                          onChange={(e) =>
                            setForm({ ...form, title: e.target.value })
                          }
                          required
                        />
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="form-label small fw-medium">
                          Category <span className="text-danger">*</span>
                        </label>
                        <select
                          className="form-select"
                          value={form.grvcatid}
                          onChange={(e) =>
                            setForm({ ...form, grvcatid: e.target.value })
                          }
                          required
                        >
                          <option value="">Select Category</option>
                          {categories.map((c) => (
                            <option key={c.grvcatid} value={c.grvcatid}>
                              {c.grvcategory}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="form-label small fw-medium">
                          Department
                        </label>
                        <select
                          className="form-select"
                          value={form.deptid}
                          onChange={(e) =>
                            setForm({ ...form, deptid: e.target.value })
                          }
                        >
                          <option value="">Select Department</option>
                          {departments.map((d) => (
                            <option key={d.deptid} value={d.deptid}>
                              {d.deptname}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="form-label small fw-medium">
                          Priority
                        </label>
                        <select
                          className="form-select"
                          value={form.priority}
                          onChange={(e) =>
                            setForm({ ...form, priority: e.target.value })
                          }
                        >
                          {["LOW", "MEDIUM", "HIGH", "CRITICAL"].map((p) => (
                            <option key={p} value={p}>
                              {p}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="form-label small fw-medium">
                          Assign To
                        </label>
                        <select
                          className="form-select"
                          value={form.assignee_user_id}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              assignee_user_id: e.target.value,
                            })
                          }
                        >
                          <option value="">Select User</option>
                          {assignableUsers.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.name} ({u.role})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-12">
                        <label className="form-label small fw-medium">
                          Description
                        </label>
                        <textarea
                          className="form-control"
                          rows={3}
                          placeholder="Describe the grievance..."
                          value={form.description}
                          onChange={(e) =>
                            setForm({ ...form, description: e.target.value })
                          }
                        />
                      </div>

                      <div className="col-12">
                        <label className="form-label small fw-medium">
                          Reporter Address
                        </label>
                        <textarea
                          className="form-control"
                          rows={2}
                          placeholder="Reporter address..."
                          value={form.reporter_address}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              reporter_address: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                  </div>

                  {/* ── File Attachments ── */}
                  <div
                    className="p-3 rounded-3 mt-3"
                    style={{ background: "#f0f4ff" }}
                  >
                    <p
                      className="small fw-semibold mb-2"
                      style={{ color: "#1a3c5e" }}
                    >
                      <i className="bi bi-paperclip me-1"></i>
                      Attach Documents
                      <span
                        className="text-muted ms-2"
                        style={{ fontSize: "11px" }}
                      >
                        (JPG, PNG, PDF — max 2MB each — multiple files allowed)
                      </span>
                    </p>

                    <input
                      type="file"
                      ref={fileInputRef}
                      className="form-control form-control-sm mb-2"
                      accept=".jpg,.jpeg,.png,.pdf"
                      multiple
                      onChange={handleFileAdd}
                    />

                    {attachments.length > 0 && (
                      <div className="mt-2">
                        <div className="small text-muted mb-1 fw-medium">
                          {attachments.length} file(s) attached:
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                          {attachments.map((att, idx) => (
                            <div
                              key={idx}
                              className="border rounded-3 p-2 bg-white d-flex align-items-center gap-2"
                              style={{ maxWidth: "200px" }}
                            >
                              {att.type === "image" && att.preview ? (
                                <img
                                  src={att.preview}
                                  alt={att.name}
                                  style={{
                                    width: 40,
                                    height: 40,
                                    objectFit: "cover",
                                    borderRadius: 4,
                                  }}
                                />
                              ) : (
                                <i className="bi bi-file-earmark-pdf text-danger fs-5"></i>
                              )}
                              <div className="flex-grow-1 overflow-hidden">
                                <div
                                  className="small fw-medium text-truncate"
                                  style={{ maxWidth: "110px" }}
                                >
                                  {att.name}
                                </div>
                                <div
                                  className="text-muted"
                                  style={{ fontSize: "10px" }}
                                >
                                  {(att.size / 1024).toFixed(0)} KB
                                </div>
                              </div>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger p-0 px-1"
                                onClick={() => removeAttachment(idx)}
                              >
                                <i className="bi bi-x"></i>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="modal-footer border-0 px-4 pb-4">
                  <button
                    type="button"
                    className="btn btn-light px-4"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn text-white px-4"
                    style={{
                      background: "linear-gradient(135deg, #1a3c5e, #2d6a4f)",
                    }}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-lg me-2"></i>
                        {editItem ? "Update" : "Save"}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Grievances;
